import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useIntl } from "react-intl"
import styled, { useTheme } from "styled-components"
import { Button, Window, HeadingBold, Icon, TertiaryCtaButton, WindowState, InvertTheme } from "slate-ui-components"
import { BuildData, copyLink, saveBuild, share } from "@zerolight-core/iframe-communications"
import { useAnalytics } from "../AnalyticsProvider"
import { getBuildData } from "../helpers"
import { useBuildSession } from "../BuildSession"
import { useDirect2D } from "../components/Direct2D"
import { useAppSelector } from "../store/hooks"
import { VisualizerSlice } from "../store/reducers/visualizer"
import { ZLVEVisualizerState } from "../types/VisualizerTypes"
import { LoadingOverlay } from "../LoadingOverlay"
import { JSONEquality } from "../utils/equal"
import { getInitialUrlArgs } from "../UrlArgsProvider"
import { useDispatch } from "react-redux"
import { useMediaQuery } from "@react-hook/media-query"
import { AppConfig } from "../config"
import { AnimationStates } from "@zerolight-core/libzl"


interface ShareDialog {
	open: () => void
	close: () => void
	element: ReactNode
}

const CloseButton = styled(Button)`
	align-self: flex-end;
	margin: -15px;
	padding: 15px;
	--icon-foreground: ${({theme}) => theme.colours.text.primary};
`

const ShareContentContainer  = styled.div<{$landscape: boolean}>`
	display: flex;
	flex-direction:  ${(p) => p.$landscape ? "row" : "column"};
	gap:7px
`

const HeroPlaceholder = styled.div`
	width: 100%;
	min-height: 0;
	max-width: 45vh;
	overflow: hidden;
	aspect-ratio: 1;
	margin: 5px auto;
	position: relative;
`

const HeroImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: contain;
	position: absolute;
`

const CTAs = styled.div`
	display: flex;
	flex-flow: column nowrap;
	gap: 7px;
	align-items: stretch;
	justify-content: center;	
`

const canShare = true;
/*
try {
	canShare = navigator.canShare({url: "https://zerolight.com"})
}
catch (ex: unknown) {
	console.log("Browser doesn't support the Web Share API", ex)
}
	*/

export const useShareDialog = (from: string) : ShareDialog => {
	const intl = useIntl()
	const {record} = useAnalytics()
	const dispatch = useDispatch()
	const {current: build, masterModel} = useBuildSession()
	const d2d = useDirect2D()
	const [windowState, updateWindowState] = useState<WindowState>("closed")
	const [copied, setCopied] = useState<"not copied" | "copying"| "copied">("not copied")
	const [saving, setSaving] = useState<boolean>(false)
	const saved = useAppSelector(s => s.configuration.saved)
	const [buildData, setBuildData] = useState<BuildData | undefined | null>()
	const { environment, prcodes } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, JSONEquality);
	const environmentAnimations = AppConfig.visualizer.configuration.optionsGroups.environments.options[environment]?.visualizerState?.forceAnimationStates as (AnimationStates | undefined)
	
	const { customWraps, appliedWraps } = useAppSelector(s => s.configuration.currentVisualizerConfig.configuration)

	const {sharing, shareFile} = getInitialUrlArgs()
	
	const activeCustomWrap = Object.entries(customWraps).find(([,wrap]) => wrap.selected)?.[0]
			

	const open = useCallback(() => {
		updateWindowState(prevState => prevState !== "open" ? "opening" : "open")
		record?.(["Configurator", "ShareOpen"], {from})
	}, [record, from])
	
	const close = useCallback(() => {
		updateWindowState(prevState => prevState !== "closed" ? "closing" : "closed")
		record?.(["Configurator", "ShareClose"])
	}, [record])

	useEffect(() => {
		setCopied("not copied")
		setBuildData(undefined)
	}, [build, activeCustomWrap, environment])

	const gatherBuildData = useCallback(async () => {
		if(buildData === undefined && build) {
			setBuildData(null)
			//Regenerate
			setBuildData(await getBuildData(intl, masterModel!, prcodes, environmentAnimations, environment, build, customWraps, appliedWraps, d2d, true))

			record?.(["Configurator", "ShareReady"])
		}
	}, [buildData, build, intl, masterModel, prcodes, environmentAnimations, environment, customWraps, appliedWraps, d2d, record])

	useEffect(() => {
		if(windowState === "closed") {
			return;
		}
		gatherBuildData()
	}, [windowState, gatherBuildData])

	

	const theme = useTheme();
	const isFullScreenLayout = useMediaQuery(theme.breakpoints.fullScreenStream);
	

	const element = useMemo(() => {
		if(windowState === "closed") {
			return null;
		}

		const doCopyLink = async () => {
			if(buildData) {
				setCopied("copying")
				await copyLink(buildData)
				setCopied("copied")
				record?.(["Configurator", "ShareLinkCopied"], {prcodes, configuration: build?.getSelectedItems(true).map(i => i.id)})
			}
		}

		const doSaveBuild = async () => {
			if(buildData) {
				setSaving(true)
				try {
					await saveBuild(buildData)
					dispatch(VisualizerSlice.actions.setSaved(true))
				} catch {
					dispatch(VisualizerSlice.actions.setSaved(false))
				}
				setSaving(false)
				record?.(["Configurator", "ShareSaveBuild"], {prcodes, configuration: build?.getSelectedItems(true).map(i => i.id)})
			}
		}

		const doShareSocial = async () => {
			if(buildData) {
				await share(buildData)
				record?.(["Configurator", "ShareSocial"], {prcodes, configuration: build?.getSelectedItems(true).map(i => i.id)})
			}
		}

		const doShareFileSocial = async () => {
			if(buildData) {
				const imageResponse = await fetch(buildData.imageUrl)
				const imageBlob = await imageResponse.blob()
				const files = [new File([imageBlob], "image.jpg", {lastModified: Date.now(), type: imageBlob.type})]
				await share(buildData, files)
				record?.(["Configurator", "ShareSocialFile"], {prcodes, configuration: build?.getSelectedItems(true).map(i => i.id)})
			}
		}

		const shareTitle = intl.formatMessage({id: "share.title", defaultMessage: "AWESOME CHOICE!"})
		const copyLinkLabel = copied === "copied" ? 
			intl.formatMessage({id: "share.copied_link", defaultMessage: "COPIED LINK"})
			:
			intl.formatMessage({id: "share.copy_link", defaultMessage: "COPY LINK"})
		const saveBuildLabel = saved || saving ? 
			intl.formatMessage({id: "share.saved_build", defaultMessage: "SAVED"})
			:
			intl.formatMessage({id: "share.save_build", defaultMessage: "SAVE"})
		const shareSocialLabel = intl.formatMessage({id: "share.justShare", defaultMessage: "SHARE"})
		const shareFileLabel = intl.formatMessage({id: "share.shareFile", defaultMessage: "SHARE DESIGN"})
		

		return (
			<InvertTheme>
				<Window state={windowState} updateState={updateWindowState} title={shareTitle}>
					<CloseButton label={intl.formatMessage({id: "general.close", defaultMessage: "Close"})} testId="share.close" onClick={close}>
						<Icon icon="Close"/>
					</CloseButton>
					<HeadingBold>
						{shareTitle}
					</HeadingBold>
					<ShareContentContainer $landscape={isFullScreenLayout}>
						<HeroPlaceholder>
							{buildData?.imageUrl && <HeroImage src={buildData.imageUrl} role="presentation"/>}
							<LoadingOverlay busy={!buildData}/>
						</HeroPlaceholder>
						<CTAs>
							{!shareFile &&
								<TertiaryCtaButton label={copyLinkLabel} testId="copy-link" disabled={!sharing || !buildData || copied === "copying"} onClick={doCopyLink}>
									{copyLinkLabel}
									<Icon icon="Copy"/>
								</TertiaryCtaButton>
							}
							<TertiaryCtaButton label={saveBuildLabel} testId="save-build" disabled={!buildData || saved || saving} onClick={doSaveBuild}>
								{saveBuildLabel}
								<Icon icon="Heart"/>
							</TertiaryCtaButton>
							{canShare &&
								<TertiaryCtaButton label={shareSocialLabel} testId="share-social" disabled={!sharing || !buildData} onClick={doShareSocial}>
									{shareSocialLabel}
									<Icon icon="Share"/>
								</TertiaryCtaButton>
							}
							{canShare && shareFile &&
								<TertiaryCtaButton label={shareSocialLabel} testId="share-social-file" disabled={!sharing || !buildData} onClick={doShareFileSocial}>
									{shareFileLabel}
									<Icon icon="Share"/>
								</TertiaryCtaButton>
							}
						</CTAs>
					</ShareContentContainer>
				</Window>
			</InvertTheme>
		)
	}, [windowState, intl, copied, saved, saving, close, isFullScreenLayout, buildData, sharing, shareFile, record, prcodes, build, dispatch])

	return {
		open,
		close,
		element
	}
}